import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { contacts, isApiError } from '@repletoryreach/sdk-api';
import { Badge, Button, Card, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Drawer } from '@repletoryreach/sdk-ui/overlays';
import { Alert, Tabs } from '@repletoryreach/sdk-ui/data-display';
import { TextInput, Select, Switch, Textarea, PhoneInput } from '@repletoryreach/sdk-ui/inputs';
import { formatPhone } from '@repletoryreach/sdk-utils/phone';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useI18n } from '../../../i18n/I18nProvider';
import { languageOptions, useLanguages } from '../../../lib/languages';
import { phoneLabels, phoneRefusalText } from '../../../lib/phone';
import { useSmsStatus } from '../../../lib/sms';
import { TagPicker, TAGS_KEY } from './TagPicker';
import { ContactIdentity } from './ContactAvatar';
import {
  OptInPill, SmsConsentPill, smsConsentOf, type SmsConsent,
  EmailConsentPill, emailConsentOf, type EmailConsent
} from './ContactTag';
import { ConversationsTab } from './tabs/ConversationsTab';
import { ConsentTab } from './tabs/ConsentTab';
import { DangerTab } from './tabs/DangerTab';
import { HistoryTab } from './tabs/HistoryTab';
import { ChangesBanner } from './ContactChanges';
import { FieldsTab, useContactFields, countFilled, isSet, localProblem, type FieldValues } from './tabs/FieldsTab';

export type DrawerTarget = contacts.Contact | { id: string } | 'new' | null;

/**
 * The whole contact, in a drawer.
 *
 * Clicking a row opens the drawer, which keeps the list on screen, so working
 * through a filtered set is one click per person rather than a round trip.
 * Open in the row's menu, or Open full page in the drawer, shows the same
 * contact on a page of its own at /contacts/:id (8 October 2026).
 *
 * Creating and viewing are the same component deliberately. They are the same
 * fields, and the drawer that saves a new contact should be the drawer that
 * edits an existing one.
 */
export function ContactDrawer({
  contact,
  onClose,
  onSaved,
  layout = 'drawer',
  prefill
}: {
  contact: DrawerTarget;
  onClose: () => void;
  onSaved: () => void;
  /**
   * What a new contact starts with (15 October 2026): the inbox opens this drawer over a conversation with the
   * person's number, name and address already known, so adding them is a check and a Save.
   */
  prefill?: { msisdn?: string | null; name?: string | null; email?: string | null };
  /**
   * `page` (8 October 2026): the same contact on a page of its own, at /contacts/:id, with every tab the drawer
   * has.
   */
  layout?: 'drawer' | 'page';
}) {
  const { t, locale: consoleLocale } = useI18n();
  // The languages a chatbot can speak (1 November 2026): the one list a contact's language is chosen from.
  const { languages } = useLanguages();
  const nav = useNavigate();
  const page = layout === 'page';
  const qc = useQueryClient();
  const isNew = contact === 'new';
  const open = contact !== null;

  const targetId = contact && contact !== 'new' ? contact.id : null;
  // `updated_at`, not `msisdn`: the service leaves a null out of the JSON, so
  // an email-only row (4 October 2026) has no `msisdn` key at all.
  const hasFullRow = !!contact && contact !== 'new' && 'updated_at' in contact;

  /*
   * A row from the list is already the whole contact, so opening from there
   * paints immediately; a deep link carries only an id, so that case fetches.
   * The query also refreshes what the row had, which is how a change made on
   * the Consent or Danger tab shows on the Details tab without a reload.
   */
  const detail = useQuery({
    queryKey: ['contact', targetId],
    queryFn: () => contacts.getContact(targetId!),
    enabled: open && !!targetId,
    select: (r) => r.data
  });

  const existing = detail.data ?? (hasFullRow ? (contact as contacts.Contact) : null);

  // The canonical form the phone field hands back ('' while it does not read).
  const [msisdn, setMsisdn] = useState('');
  const [msisdnBad, setMsisdnBad] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [locale, setLocale] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [optIn, setOptIn] = useState(true);
  // SMS consent is its own answer, never inferred from WhatsApp consent.
  // 'unset' sends nothing, so a contact nobody asked stays "not recorded".
  const [smsConsent, setSmsConsent] = useState<SmsConsent>('unset');
  const { available: smsAvailable } = useSmsStatus();
  /*
   * Email consent (4 October 2026), the same three states. Shown whether or
   * not email sending is live: an address on a contact is useful either way,
   * and an answer recorded now is one nobody has to ask for later.
   */
  const [emailConsent, setEmailConsent] = useState<EmailConsent>('unset');
  const [emailError, setEmailError] = useState('');
  // Neither a number nor an address: said under the number, cleared by either.
  const [keyMissing, setKeyMissing] = useState(false);
  // Custom fields, keyed by definition key; undefined is "not set".
  const [fields, setFields] = useState<FieldValues>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msisdnError, setMsisdnError] = useState('');
  const [tab, setTab] = useState('details');
  const { defs } = useContactFields();
  const activeDefs = defs.filter((d) => d.is_active);
  const filled = countFilled(fields, activeDefs);

  // §5: this ran during render, which is a side effect in the render phase.
  const formKey = existing?.id ?? (isNew ? 'new' : null);
  useEffect(() => {
    if (!open) return;
    setMsisdn(existing?.msisdn ?? (isNew ? prefill?.msisdn ?? '' : ''));
    setMsisdnBad(false);
    setName(existing?.name ?? (isNew ? prefill?.name ?? '' : ''));
    setEmail(existing?.email ?? (isNew ? prefill?.email ?? '' : ''));
    setLocale(existing?.locale ?? '');
    setTags(existing?.tags ?? []);
    setNotes(existing?.notes ?? '');
    setOptIn(existing?.opt_in ?? true);
    setSmsConsent(smsConsentOf(existing?.sms_opt_in));
    setEmailConsent(emailConsentOf(existing?.email_opt_in));
    setEmailError('');
    setKeyMissing(false);
    setFields({ ...(existing?.custom_fields ?? {}) });
    setFieldErrors({});
    setMsisdnError('');
  }, [open, formKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // A different contact always opens on Details, whatever the last one was on.
  useEffect(() => { if (open) setTab('details'); }, [open, targetId]);

  async function save() {
    // §5: validation belongs on the field, not in a toast the user has to
    // read and then map back to a form they can no longer see. The field
    // has already said why a number does not read; the submit only refuses
    // to carry it. A contact needs a number or an address (4 October 2026),
    // so an empty number is fine once there is an address.
    if (msisdnBad) {
      setMsisdnError(t('phone.fixNumber'));
      setTab('details');
      return;
    }
    if (!msisdn && !email.trim()) {
      setKeyMissing(true);
      setTab('details');
      return;
    }

    /*
     * The merge the service applies: a key present is set, null removes it,
     * absent leaves it. So only what changed travels: a value typed here, or
     * null for a field the person emptied that the contact used to carry.
     * Anything the input cannot express as typed is refused here, on the
     * field, before the round trip.
     */
    const custom_fields: Record<string, contacts.ContactFieldValue> = {};
    const problems: Record<string, string> = {};
    const had = existing?.custom_fields ?? {};
    for (const d of activeDefs) {
      const v = fields[d.key];
      if (isSet(v)) {
        const problem = localProblem(t, d, v);
        if (problem) problems[d.key] = problem;
        else if (v !== had[d.key]) custom_fields[d.key] = v as contacts.ContactFieldValue;
      } else if (d.key in had) {
        custom_fields[d.key] = null;
      }
    }
    if (Object.keys(problems).length > 0) {
      setFieldErrors(problems);
      setTab('fields');
      return;
    }

    setBusy(true);
    try {
      await contacts.upsertContact({
        msisdn: msisdn || undefined,
        name: name || undefined,
        email: email.trim() || undefined,
        locale: locale || undefined,
        tags,
        notes,
        opt_in: optIn,
        // Only an answer that changed travels: the service journals each one.
        ...(smsAvailable && smsConsent !== 'unset' && smsConsent !== smsConsentOf(existing?.sms_opt_in)
          ? { sms_opt_in: smsConsent === 'yes' }
          : {}),
        ...(emailConsent !== 'unset' && emailConsent !== emailConsentOf(existing?.email_opt_in)
          ? { email_opt_in: emailConsent === 'yes' }
          : {}),
        ...(Object.keys(custom_fields).length > 0 ? { custom_fields } : {})
      });
      notifySuccess(t('contacts.toast.saved', { msisdn: msisdn ? formatPhone(msisdn) : email.trim() }), t('contacts.title'));
      // A new tag reaches the vocabulary through this save, so the filter and
      // every other picker have to hear about it.
      qc.invalidateQueries({ queryKey: TAGS_KEY });
      if (targetId) qc.invalidateQueries({ queryKey: ['contact', targetId] });
      onSaved();
      // The drawer closes after a save; the full page stays where it is.
      if (!page) onClose();
    } catch (e) {
      const refused = phoneRefusalText(e, t);
      const fieldRefusal = fieldRefusalFor(e, activeDefs, t);
      if (refused) { setMsisdnError(refused); setTab('details'); }
      else if (isApiError(e) && e.code === 'EMAIL_INVALID') { setEmailError(t('contacts.form.emailInvalid')); setTab('details'); }
      else if (isApiError(e) && e.code === 'CONTACT_KEY_REQUIRED') { setKeyMissing(true); setTab('details'); }
      else if (fieldRefusal) { setFieldErrors(fieldRefusal); setTab('fields'); }
      else notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('toast.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  const loading = !!targetId && detail.isPending && !hasFullRow;

  /*
   * An undone change (4 October 2026) puts the old value back on the
   * contact. The form holds its own copy of the fields, keyed to the contact
   * rather than to its contents, so the value undone is written into the form
   * too; otherwise a Save straight after would send the changed value again.
   */
  function onUndone(field: string, value: string | null) {
    const v = value ?? '';
    if (field === 'name') setName(v);
    else if (field === 'email') setEmail(v);
    else if (field === 'locale') setLocale(v);
    else if (field.startsWith('field:')) setFields((prev) => ({ ...prev, [field.slice(6)]: value ?? undefined }));
  }

  const body = (
      <div className="rr-contacts__form">
        {/* Who this is, pinned above the tabs, so the header still names the
            person once the reader is three tabs deep. */}
        {!isNew && existing && (
          <div className="rr-contacts__drawer-head">
            <ContactIdentity name={existing.name} msisdn={existing.msisdn} email={existing.email} />
            <Group gap={6} wrap="wrap" justify="flex-end">
              {/* WhatsApp consent means nothing without a number. */}
              {existing.msisdn && <OptInPill optedIn={existing.opt_in} />}
              {smsAvailable && existing.msisdn && <SmsConsentPill consent={smsConsentOf(existing.sms_opt_in)} />}
              <EmailConsentPill consent={emailConsentOf(existing.email_opt_in)} />
              {!page && (
                <Button size="xs" variant="subtle" onClick={() => nav(`/contacts/${encodeURIComponent(existing.id)}`)} data-testid="contact-open-page">
                  {t('contacts.drawer.openPage')}
                </Button>
              )}
            </Group>
          </div>
        )}

        {loading && <Skeleton height={140} />}
        {!loading && detail.error && (
          <Alert color="error" role="alert">{t('contacts.view.notFound')}</Alert>
        )}

        {!loading && !detail.error && (
          <Tabs value={tab} onChange={setTab}>
            <Tabs.List>
              <Tabs.Tab value="details">{t('contacts.form.tab.details')}</Tabs.Tab>
              {/* The extra details the organisation keeps; the badge is how
                  many of them this person has, so a reader knows whether the
                  tab is worth opening. */}
              <Tabs.Tab
                value="fields"
                rightSection={filled > 0 ? <Badge size="xs" variant="light">{filled}</Badge> : undefined}
              >
                {t('contacts.form.tab.fields')}
              </Tabs.Tab>
              <Tabs.Tab value="notes">{t('contacts.form.tab.notes')}</Tabs.Tab>
              {/* A contact that does not exist yet has no conversation, no
                  consent trail and nothing to delete. */}
              {/* Conversations are WhatsApp threads, found by number; an
                  email-only contact has none. */}
              {existing?.msisdn && <Tabs.Tab value="conversations">{t('contacts.view.tabs.conversations')}</Tabs.Tab>}
              {existing && <Tabs.Tab value="consent">{t('contacts.view.tabs.consent')}</Tabs.Tab>}
              {/* What changed on this contact, from what, and who changed it. */}
              {existing && <Tabs.Tab value="history">{t('contacts.view.tabs.history')}</Tabs.Tab>}
              {existing && <Tabs.Tab value="danger">{t('contacts.view.tabs.danger')}</Tabs.Tab>}
            </Tabs.List>

            <Tabs.Panel value="details">
              <Stack gap="md" style={{ paddingTop: 14 }} data-guide="contact-details">
                {/* A flow's changes nobody has looked at yet: never silent. */}
                {existing && <ChangesBanner contactId={existing.id} onUndone={onUndone} />}
                <TextInput
                  label={t('contacts.form.fullName')}
                  placeholder={t('contacts.form.fullName.placeholder')}
                  value={name}
                  onChange={(e) => setName(e.currentTarget.value)}
                />

                {/* One field: the person types the number as they would dial
                    it, the picker says which country, and the line under it
                    says what will be saved. The number is the contact's key,
                    so it is read-only once the contact exists, except on an
                    email-only contact, which can be given one. */}
                <PhoneInput
                  label={t('contacts.form.phone')}
                  description={t('phone.hint')}
                  error={msisdnError || (keyMissing ? t('contacts.form.keyRequired') : '')}
                  value={msisdn}
                  onChange={(e164, meta) => { setMsisdn(e164); setMsisdnBad(!meta.valid && meta.raw.trim() !== ''); setMsisdnError(''); setKeyMissing(false); }}
                  labels={phoneLabels(t)}
                  disabled={!isNew && !!existing?.msisdn}
                />

                {/* The address is the key of an email-only contact, so like
                    the number it is read-only there: changing it would mean
                    a different person. */}
                <TextInput
                  label={t('common.email')}
                  type="email"
                  placeholder="mary@example.com"
                  value={email}
                  error={emailError || undefined}
                  onChange={(e) => { setEmail(e.currentTarget.value); setEmailError(''); setKeyMissing(false); }}
                  disabled={!isNew && !!existing && !existing.msisdn}
                />

                <div className="rr-contacts__form-row">
                  {/* §4 item 9: `locale` was never editable anywhere, so a
                      contact's language could only be set by a CSV import.
                      Since 1 November 2026 the languages come from the list a
                      chatbot speaks from, each in its own name (an endonym is
                      never translated), and a stored value the list does not
                      have (an old "en-ZA" from an import) shows as it is. */}
                  <Select
                    label={t('contacts.columns.locale')}
                    description={t('contacts.form.localeHint')}
                    value={locale}
                    onChange={(e) => setLocale(e.currentTarget.value)}
                    data={[
                      { value: '', label: t('contacts.form.localeUnset') },
                      ...languageOptions(languages, consoleLocale, locale)
                    ]}
                  />
                  <Switch
                    label={t('contacts.form.optIn')}
                    description={t('contacts.form.optInHint')}
                    checked={optIn}
                    onChange={(next) => setOptIn(next)}
                  />
                </div>

                {smsAvailable && (
                  <Select
                    label={t('contacts.form.smsOptIn')}
                    description={t('contacts.form.smsOptInHint')}
                    value={smsConsent}
                    onChange={(e) => setSmsConsent(e.currentTarget.value as SmsConsent)}
                    data={[
                      // A recorded answer cannot be taken back to "not
                      // recorded", only changed, so that choice is offered
                      // only while nothing is recorded.
                      ...(smsConsentOf(existing?.sms_opt_in) === 'unset'
                        ? [{ value: 'unset', label: t('contacts.form.smsOptIn.unset') }]
                        : []),
                      { value: 'yes', label: t('contacts.form.smsOptIn.yes') },
                      { value: 'no', label: t('contacts.form.smsOptIn.no') }
                    ]}
                  />
                )}

                <Select
                  label={t('contacts.form.emailOptIn')}
                  description={t('contacts.form.emailOptInHint')}
                  value={emailConsent}
                  onChange={(e) => setEmailConsent(e.currentTarget.value as EmailConsent)}
                  data={[
                    // As with SMS: "not recorded" is offered only while
                    // nothing is recorded.
                    ...(emailConsentOf(existing?.email_opt_in) === 'unset'
                      ? [{ value: 'unset', label: t('contacts.form.emailOptIn.unset') }]
                      : []),
                    { value: 'yes', label: t('contacts.form.emailOptIn.yes') },
                    { value: 'no', label: t('contacts.form.emailOptIn.no') }
                  ]}
                />

                <TagPicker
                  id="contact-tags"
                  label={t('inbox.tags.title')}
                  description={t('contacts.form.tagsHint')}
                  value={tags}
                  onChange={setTags}
                />
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="fields">
              <div style={{ paddingTop: 14 }}>
                <FieldsTab
                  values={fields}
                  errors={fieldErrors}
                  onChange={(key, value) => {
                    setFields((prev) => ({ ...prev, [key]: value }));
                    setFieldErrors((prev) => {
                      if (!(key in prev)) return prev;
                      const next = { ...prev };
                      delete next[key];
                      return next;
                    });
                  }}
                />
              </div>
            </Tabs.Panel>

            <Tabs.Panel value="notes">
              <Stack gap="sm" style={{ paddingTop: 14 }}>
                <Textarea
                  label={t('contacts.form.notes')}
                  description={t('contacts.form.notesHint')}
                  placeholder={t('contacts.form.notes.placeholder')}
                  value={notes}
                  onChange={(e) => setNotes(e.currentTarget.value)}
                  minRows={10}
                />
                <Text role="caption" c="var(--rr-color-text-muted)">{t('contacts.form.notes.private')}</Text>
              </Stack>
            </Tabs.Panel>

            {existing?.msisdn && (
              <Tabs.Panel value="conversations">
                <div style={{ paddingTop: 14 }}><ConversationsTab msisdn={existing.msisdn} /></div>
              </Tabs.Panel>
            )}

            {existing && (
              <Tabs.Panel value="consent">
                <div style={{ paddingTop: 14 }}><ConsentTab contactId={existing.id} /></div>
              </Tabs.Panel>
            )}

            {existing && (
              <Tabs.Panel value="history">
                <div style={{ paddingTop: 14 }}><HistoryTab contactId={existing.id} onUndone={onUndone} /></div>
              </Tabs.Panel>
            )}

            {existing && (
              <Tabs.Panel value="danger">
                <div style={{ paddingTop: 14 }}>
                  <DangerTab contact={existing} onDeleted={onClose} />
                </div>
              </Tabs.Panel>
            )}
          </Tabs>
        )}

        {/* Save only belongs to the three tabs that edit. On Consent or Danger
            it would be a button with nothing to save. */}
        {(tab === 'details' || tab === 'fields' || tab === 'notes') && !loading && (
          <Group justify="flex-end" className="rr-contacts__form-actions" data-guide="contact-save">
            <Button variant="default" onClick={onClose}>{t('action.cancel')}</Button>
            <Button onClick={save} loading={busy}>{t('contacts.form.save')}</Button>
          </Group>
        )}
      </div>
  );

  if (page) {
    // The same tabs as the drawer, with the room of a whole page.
    return <Card withBorder className="rr-contact-page">{body}</Card>;
  }

  return (
    <Drawer
      opened={open}
      onClose={onClose}
      title={isNew ? t('contacts.drawer.new') : t('contacts.drawer.view')}
      position="right"
      // Wider than a form drawer: this one carries a conversation list and a
      // consent trail, and both are unreadable squeezed into 420px.
      size={620}
      className="rr-contacts__drawer"
    >
{body}
    </Drawer>
  );
}

/**
 * A custom-field refusal from the service, as an error on the field it names.
 * The service says which key and why (UNKNOWN_FIELD, FIELD_TYPE_MISMATCH,
 * FIELD_OPTION_UNKNOWN); the sentence shown is the console's own, per type,
 * so it reads in the person's language.
 */
function fieldRefusalFor(
  e: unknown,
  defs: contacts.ContactFieldDefinition[],
  t: (k: string) => string
): Record<string, string> | null {
  if (!isApiError(e)) return null;
  if (e.code !== 'UNKNOWN_FIELD' && e.code !== 'FIELD_TYPE_MISMATCH' && e.code !== 'FIELD_OPTION_UNKNOWN') return null;
  const body = e.body as { error?: { field?: string } } | undefined;
  const key = body?.error?.field;
  if (!key) return null;
  const def = defs.find((d) => d.key === key);
  const message = e.code === 'UNKNOWN_FIELD' || !def
    ? t('contacts.form.fields.invalid.unknown')
    : t(`contacts.form.fields.invalid.${def.type === 'text' ? 'unknown' : def.type}`);
  return { [key]: message };
}
